import React, { useState, useEffect, useRef } from 'react';
import { XMarkIcon, ShoppingCartIcon, TruckIcon, CheckCircleIcon, CreditCardIcon } from '../icons/HeroIcons';
import { StripePaymentForm } from './StripePaymentForm';
import { SavedCardsPayment } from './SavedCardsPayment';
import { AISHA_API_BASE } from '../../constants';
import { ArtisanProduct, ArtisanProductVariation } from '@/services/api/artisan-product';

interface ArtisanPurchaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  imageUrl: string;
  product: ArtisanProduct;
  onPurchaseCompleted?: (orderId?: number) => void;
}

interface ShippingAddress {
  name: string;
  email: string;
  phone: string;
  zip: string;
  state_name: string;
  city: string;
  address1: string;
  address2: string;
}

const JAPAN_PREFECTURES = [
  '北海道',
  '青森県', '岩手県', '宮城県', '秋田県', '山形県', '福島県',
  '茨城県', '栃木県', '群馬県', '埼玉県', '千葉県', '東京都', '神奈川県',
  '新潟県', '富山県', '石川県', '福井県', '山梨県', '長野県',
  '岐阜県', '静岡県', '愛知県', '三重県',
  '滋賀県', '京都府', '大阪府', '兵庫県', '奈良県', '和歌山県',
  '鳥取県', '島根県', '岡山県', '広島県', '山口県',
  '徳島県', '香川県', '愛媛県', '高知県',
  '福岡県', '佐賀県', '長崎県', '熊本県', '大分県', '宮崎県', '鹿児島県',
  '沖縄県'
];

const ArtisanPurchaseModal: React.FC<ArtisanPurchaseModalProps> = ({
  isOpen,
  onClose,
  userId,
  imageUrl,
  product,
  onPurchaseCompleted
}) => {
  // Steps: 1=商品確認・バリエーション・施工選択, 2=配送先入力, 3=確認・決済, 4=完了
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedVariation, setSelectedVariation] = useState<ArtisanProductVariation | null>(null);
  const [withInstallation, setWithInstallation] = useState(false);
  const [shippingAddress, setShippingAddress] = useState<ShippingAddress>({
    name: '', email: '', phone: '', zip: '', state_name: '', city: '', address1: '', address2: ''
  });
  const [saveAddress, setSaveAddress] = useState(true);
  const [loading, setLoading] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [error, setError] = useState('');
  const [, setOrderResult] = useState<any>(null);
  const [createdOrderId, setCreatedOrderId] = useState<number | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [paymentIntentId, setPaymentIntentId] = useState<string | null>(null);
  const [totalWithTax, setTotalWithTax] = useState(0);
  const [showStripePayment, setShowStripePayment] = useState(false);
  const [orderMemo, setOrderMemo] = useState('');
  const [savedCards, setSavedCards] = useState<any[]>([]);
  const modalRef = useRef<HTMLDivElement>(null);
  const [useNewCard, setUseNewCard] = useState(false);

  const fetchSavedCards = async () => {
    if (!userId) return;
    try {
      const response = await fetch(`${AISHA_API_BASE}/stripe/saved-cards/?user_id=${userId}`);
      const data = await response.json();
      if (data.success) {
        setSavedCards(data.payment_methods || []);
      }
    } catch (err) {
      console.error('保存済みカード取得エラー:', err);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setCurrentStep(1);
      // デフォルトで最初のバリエーション（Sサイズ）を選択
      if (product.variations && product.variations.length > 0) {
        setSelectedVariation(product.variations[0]);
      } else {
        setSelectedVariation(null);
      }
      setWithInstallation(false);
      setError('');
      setOrderResult(null);
      setCreatedOrderId(null);
      setClientSecret(null);
      setShowStripePayment(false);
      setUseNewCard(false);
      fetchSavedAddress();
      fetchSavedCards();
    }
  }, [isOpen, userId]);

  const fetchSavedAddress = async () => {
    if (!userId) return;
    try {
      const response = await fetch(`${AISHA_API_BASE}/shipping-address/?user_id=${userId}`);
      const data = await response.json();
      if (data.success && data.has_address) {
        setShippingAddress(data.address);
      }
    } catch (err) {
      console.error('住所取得エラー:', err);
    }
  };

  const saveShippingAddress = async () => {
    if (!saveAddress) return;
    try {
      await fetch(`${AISHA_API_BASE}/shipping-address/save/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, address: shippingAddress })
      });
    } catch (err) {
      console.error('住所保存エラー:', err);
    }
  };

  // 価格計算（税込）
  const getProductPrice = () => {
    let price = product.product_price;
    if (selectedVariation) {
      price += selectedVariation.price;
    }
    return Math.floor(price * 1.1);
  };

  const getInstallationPrice = () => {
    if (!withInstallation) return 0;
    let price = product.installation_price;
    if (selectedVariation) {
      price += selectedVariation.installation_price;
    }
    return Math.floor(price * 1.1);
  };

  const getTotalPrice = () => {
    return getProductPrice() + getInstallationPrice();
  };

  const validateShippingForm = () => {
    return shippingAddress.name &&
           shippingAddress.email &&
           shippingAddress.phone &&
           shippingAddress.zip &&
           shippingAddress.state_name &&
           shippingAddress.city &&
           shippingAddress.address1;
  };

  const handleNextStep = async () => {
    if (currentStep === 1) {
      setCurrentStep(2);
    } else if (currentStep === 2) {
      await saveShippingAddress();
      setCurrentStep(3);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setShowStripePayment(false);
    }
  };

  const handleStartPayment = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${AISHA_API_BASE}/artisan/create-payment-intent/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          product_id: product.id,
          variation: selectedVariation ? selectedVariation.name : null,
          with_installation: withInstallation,
          amount: getTotalPrice(),
          image_url: imageUrl,
          recipient: { ...shippingAddress, country_code: 'JP' },
          memo: orderMemo
        })
      });
      const data = await response.json();
      if (data.success && data.client_secret) {
        setClientSecret(data.client_secret);
        setPaymentIntentId(data.payment_intent_id);
        setTotalWithTax(data.amount);
        if (data.order_id) setCreatedOrderId(data.order_id);
        setShowStripePayment(true);
      } else {
        setError(data.error || '決済の準備に失敗しました');
      }
    } catch (err) {
      setError('決済の準備中にエラーが発生しました');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = async () => {
    setPaymentProcessing(true);
    // 決済完了をバックエンドに通知してステータスをnewに更新
    try {
      await fetch(`${AISHA_API_BASE}/artisan/confirm-payment/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          order_id: createdOrderId,
          payment_intent_id: paymentIntentId,
        })
      });
    } catch (err) {
      console.error('決済確認エラー:', err);
    }
    if (modalRef.current) modalRef.current.scrollTop = 0;
    setOrderResult({ success: true });
    setCurrentStep(4);
    if (onPurchaseCompleted) {
      onPurchaseCompleted(createdOrderId || undefined);
    }
    setPaymentProcessing(false);
  };

  const handleClose = () => {
    onClose();
  };

  if (!isOpen) return null;

  const stepIcons = [
    { icon: <ShoppingCartIcon className="w-5 h-5" />, label: '商品確認' },
    { icon: <TruckIcon className="w-5 h-5" />, label: '請求/発送先' },
    { icon: <CreditCardIcon className="w-5 h-5" />, label: '確認・決済' },
    { icon: <CheckCircleIcon className="w-5 h-5" />, label: '完了' },
  ];

  const inputClass = "w-full p-3 border border-gray-600 rounded-lg bg-gray-700 text-gray-200 placeholder-gray-500 focus:ring-orange-500 focus:border-orange-500 text-sm";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[65] px-4 py-[env(safe-area-inset-top,1rem)] pb-[env(safe-area-inset-bottom,1rem)]">
      {paymentProcessing && (
        <div
          className="fixed inset-0 bg-black bg-opacity-80 flex flex-col items-center justify-center z-[100]"
          onClick={(e) => e.stopPropagation()}
        >
          <svg className="animate-spin h-14 w-14 text-orange-400 mb-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-white text-lg font-bold mb-1">決済処理中…</p>
          <p className="text-gray-300 text-sm">画面を閉じずにお待ちください</p>
        </div>
      )}
      <div ref={modalRef} className="bg-gray-800 rounded-lg max-w-lg w-full max-h-[85vh] my-auto overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {/* ヘッダー */}
        <div className="sticky top-0 bg-gray-800 border-b border-gray-700 p-4 flex justify-between items-center z-10">
          <h2 className="text-xl font-bold text-white flex items-center">
            <ShoppingCartIcon className="w-6 h-6 mr-2" />
            デカールオーダー
          </h2>
          <button onClick={handleClose} className="text-gray-400 hover:text-white transition-colors">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* ステッププログレス */}
        <div className="px-4 py-3 bg-gray-750">
          <div className="flex items-center justify-between">
            {stepIcons.map((s, i) => (
              <div key={i} className={`flex-1 text-center ${currentStep >= i + 1 ? 'text-orange-400' : 'text-gray-500'}`}>
                <div className={`w-8 h-8 mx-auto rounded-full flex items-center justify-center ${currentStep >= i + 1 ? 'bg-orange-600' : 'bg-gray-700'}`}>
                  {currentStep > i + 1 ? '✓' : i + 1}
                </div>
                <span className="text-xs mt-1">{s.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="p-4">
          {/* ステップ1: 商品確認・バリエーション・施工選択 */}
          {currentStep === 1 && (
            <div className="space-y-4">
              {/* 商品情報 */}
              <div className="p-4 bg-gray-700 rounded-lg">
                {imageUrl && (
                  <div className="mb-3">
                    <img src={imageUrl} alt="生成画像" className="w-full h-48 object-contain rounded-lg border border-gray-600 bg-gray-800" />
                  </div>
                )}
                <div className="flex gap-4">
                  {product.sample_image_url && (
                    <img src={product.sample_image_url} alt={product.product_name} className="w-16 h-16 object-cover rounded-lg border border-gray-600 flex-shrink-0" />
                  )}
                  <div className="flex-1">
                    <h3 className="font-bold text-white">{product.product_name}</h3>
                    {product.description && (
                      <p className="text-sm text-gray-400 mt-1 line-clamp-2">{product.description}</p>
                    )}
                    <p className="text-lg font-bold text-orange-400 mt-1">
                      ¥{Math.floor(product.product_price * 1.1).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>

              {/* バリエーション選択 */}
              {product.variations && product.variations.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-300 mb-2">バリエーション</h4>
                  <div className="space-y-2">
                    {product.variations.map((v, i) => (
                      <label
                        key={i}
                        className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                          selectedVariation === v
                            ? 'border-orange-500 bg-orange-900/30'
                            : 'border-gray-600 bg-gray-700 hover:border-gray-500'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <input type="radio" name="variation" checked={selectedVariation === v} onChange={() => setSelectedVariation(v)} className="text-orange-500 focus:ring-orange-500" />
                          <div>
                            <span className="text-sm font-medium text-white">{v.name}</span>
                            {v.sample_image_url && <img src={v.sample_image_url} alt={v.name} className="w-10 h-10 object-cover rounded mt-1" />}
                          </div>
                        </div>
                        {v.price !== 0 && (
                          <span className="text-sm text-gray-400">{v.price > 0 ? '+' : ''}¥{Math.floor(v.price * 1.1).toLocaleString()}</span>
                        )}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* 施工有無 */}
              {product.installation_price > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-300 mb-2">施工オプション</h4>
                  <label
                    className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                      withInstallation ? 'border-orange-500 bg-orange-900/30' : 'border-gray-600 bg-gray-700 hover:border-gray-500'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <input type="checkbox" checked={withInstallation} onChange={(e) => setWithInstallation(e.target.checked)} className="rounded text-orange-500 focus:ring-orange-500" />
                      <div>
                        <span className="text-sm font-medium text-white">施工を依頼する</span>
                        {product.installation_description && <p className="text-xs text-gray-400 mt-0.5">{product.installation_description}</p>}
                        {product.installation_estimated_time && <p className="text-xs text-gray-500">予想時間: {product.installation_estimated_time}</p>}
                      </div>
                    </div>
                    <span className="text-sm font-medium text-orange-400">+¥{getInstallationPrice().toLocaleString()}</span>
                  </label>
                  <p className="text-xs text-gray-500 mt-2 leading-relaxed">※ 神奈川県川崎市または、お客様の近隣での施工店を弊社でお探し致します。（ご希望に添えない場合もございます）</p>
                </div>
              )}

              {/* 合計金額 */}
              <div className="bg-gray-700 rounded-lg p-4 space-y-1">
                <div className="flex justify-between text-sm text-gray-400">
                  <span>商品代金</span><span>¥{getProductPrice().toLocaleString()}</span>
                </div>
                {withInstallation && (
                  <div className="flex justify-between text-sm text-gray-400">
                    <span>施工代金</span><span>¥{getInstallationPrice().toLocaleString()}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-white pt-2 border-t border-gray-600">
                  <span>合計</span><span className="text-orange-400">¥{getTotalPrice().toLocaleString()}</span>
                </div>
              </div>
            </div>
          )}

          {/* ステップ2: 配送先入力 */}
          {currentStep === 2 && (
            <div className="space-y-3">
              <h3 className="font-medium text-white mb-3 flex items-center">
                <TruckIcon className="w-5 h-5 mr-2" />
                請求/発送先 情報
              </h3>
              <div>
                <label className="block text-xs text-gray-400 mb-1">お名前 *</label>
                <input type="text" value={shippingAddress.name} onChange={(e) => setShippingAddress({ ...shippingAddress, name: e.target.value })} className={inputClass} placeholder="山田 太郎" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">メールアドレス *</label>
                  <input type="email" value={shippingAddress.email} onChange={(e) => setShippingAddress({ ...shippingAddress, email: e.target.value })} className={inputClass} />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">電話番号 *</label>
                  <input type="tel" value={shippingAddress.phone} onChange={(e) => setShippingAddress({ ...shippingAddress, phone: e.target.value })} className={inputClass} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">郵便番号 *</label>
                  <input type="text" value={shippingAddress.zip} onChange={(e) => setShippingAddress({ ...shippingAddress, zip: e.target.value })} className={inputClass} placeholder="123-4567" />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">都道府県 *</label>
                  <select value={shippingAddress.state_name} onChange={(e) => setShippingAddress({ ...shippingAddress, state_name: e.target.value })} className={inputClass}>
                    <option value="">選択してください</option>
                    {JAPAN_PREFECTURES.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">市区町村 *</label>
                <input type="text" value={shippingAddress.city} onChange={(e) => setShippingAddress({ ...shippingAddress, city: e.target.value })} className={inputClass} />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">住所1 *</label>
                <input type="text" value={shippingAddress.address1} onChange={(e) => setShippingAddress({ ...shippingAddress, address1: e.target.value })} className={inputClass} />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">住所2（建物名等）</label>
                <input type="text" value={shippingAddress.address2} onChange={(e) => setShippingAddress({ ...shippingAddress, address2: e.target.value })} className={inputClass} />
              </div>
              <label className="flex items-center gap-2 mt-2 p-3 bg-gray-700 rounded-lg">
                <input type="checkbox" checked={saveAddress} onChange={(e) => setSaveAddress(e.target.checked)} className="rounded text-orange-500 focus:ring-orange-500" />
                <span className="text-sm text-gray-300">この住所を保存して次回から入力を省略する</span>
              </label>
            </div>
          )}

          {/* ステップ3: 確認・決済 */}
          {currentStep === 3 && (
            <div className="space-y-4">
              {!showStripePayment ? (
                <>
                  <h3 className="font-medium text-white">ご注文内容の確認</h3>
                  <div className="bg-gray-700 rounded-lg p-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">商品</span><span className="text-white font-medium">{product.product_name}</span>
                    </div>
                    {selectedVariation && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-400">バリエーション</span><span className="text-gray-300">{selectedVariation.name}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">商品代金</span><span className="text-gray-300">¥{getProductPrice().toLocaleString()}</span>
                    </div>
                    {withInstallation && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-400">施工代金</span><span className="text-gray-300">¥{getInstallationPrice().toLocaleString()}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-bold text-white pt-2 border-t border-gray-600">
                      <span>合計金額</span><span className="text-orange-400">¥{getTotalPrice().toLocaleString()}</span>
                    </div>
                  </div>

                  <div className="bg-gray-700 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-300 mb-2">請求/発送先</h4>
                    <div className="text-sm text-gray-400 space-y-0.5">
                      <p>{shippingAddress.name}</p>
                      <p>〒{shippingAddress.zip}</p>
                      <p>{shippingAddress.state_name}{shippingAddress.city}</p>
                      <p>{shippingAddress.address1}</p>
                      {shippingAddress.address2 && <p>{shippingAddress.address2}</p>}
                      <p>{shippingAddress.phone}</p>
                      <p>{shippingAddress.email}</p>
                    </div>
                  </div>

                  <div className="bg-gray-700 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-300 mb-1">メモ</h4>
                    <p className="text-xs text-gray-500 mb-2">愛車と貼りたいウィンドウ箇所を簡単に明記ください</p>
                    <textarea
                      value={orderMemo}
                      onChange={(e) => setOrderMemo(e.target.value)}
                      placeholder="例: ジムニーシエラ リアサイドウィンドウ左"
                      className="w-full p-3 border border-gray-600 rounded-lg bg-gray-800 text-gray-200 placeholder-gray-500 focus:ring-orange-500 focus:border-orange-500 text-base resize-none"
                      rows={3}
                    />
                  </div>

                  {error && <div className="bg-red-900/50 border border-red-500 text-red-200 text-sm p-3 rounded-lg">{error}</div>}

                  <button
                    onClick={handleStartPayment}
                    disabled={loading}
                    className="w-full bg-orange-600 text-white font-bold py-3 rounded-lg hover:bg-orange-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                  >
                    <CreditCardIcon className="w-5 h-5" />
                    {loading ? '準備中...' : `¥${getTotalPrice().toLocaleString()}（税込）を支払う`}
                  </button>
                </>
              ) : savedCards.length > 0 && !useNewCard ? (
                <div>
                  <SavedCardsPayment
                    userId={userId}
                    totalAmount={totalWithTax}
                    onSelectCard={async (cardId) => {
                      setPaymentProcessing(true);
                      try {
                        setError('');
                        const response = await fetch(`${AISHA_API_BASE}/stripe/payment-with-saved-card/`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            user_id: userId,
                            payment_method_id: cardId,
                            amount: totalWithTax,
                            credit_amount: 0
                          })
                        });
                        const data = await response.json();
                        if (data.success) {
                          try {
                            await fetch(`${AISHA_API_BASE}/artisan/confirm-payment/`, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ order_id: createdOrderId, payment_intent_id: data.payment_intent_id })
                            });
                          } catch (e) { console.error('決済確認エラー:', e); }
                          if (modalRef.current) modalRef.current.scrollTop = 0;
                          setOrderResult({ success: true });
                          setCurrentStep(4);
                          if (onPurchaseCompleted) onPurchaseCompleted(createdOrderId || undefined);
                        } else {
                          setError(data.error || '決済に失敗しました');
                        }
                      } catch (err) {
                        setError('決済処理中にエラーが発生しました');
                      } finally {
                        setPaymentProcessing(false);
                      }
                    }}
                    onPaymentSuccess={() => handlePaymentSuccess()}
                    onPaymentError={(msg) => setError(msg)}
                    onUseNewCard={() => setUseNewCard(true)}
                    onCancel={() => setShowStripePayment(false)}
                    savedCards={savedCards}
                    onCardsUpdate={fetchSavedCards}
                  />
                </div>
              ) : (
                <div>
                  {savedCards.length > 0 && (
                    <button onClick={() => setUseNewCard(false)} className="mb-3 text-orange-400 hover:text-orange-300 text-sm flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                      保存済みカードに戻る
                    </button>
                  )}
                  <h3 className="text-lg font-semibold text-white mb-3 flex items-center">
                    <CreditCardIcon className="w-5 h-5 mr-2" />
                    クレジットカード決済
                  </h3>
                  {clientSecret && (
                    <StripePaymentForm
                      userId={userId}
                      chargeAmount={totalWithTax}
                      creditAmount={0}
                      existingClientSecret={clientSecret}
                      existingPaymentIntentId={paymentIntentId || undefined}
                      defaultPostalCode={shippingAddress.zip}
                      onSuccess={() => handlePaymentSuccess()}
                      onError={(msg) => { setError(msg); setShowStripePayment(false); }}
                      onCancel={() => setShowStripePayment(false)}
                    />
                  )}
                </div>
              )}
            </div>
          )}

          {/* ステップ4: 完了 */}
          {currentStep === 4 && (
            <div className="text-center py-8 space-y-4">
              <CheckCircleIcon className="w-16 h-16 mx-auto text-green-500" />
              <h3 className="text-xl font-bold text-white">ご注文ありがとうございます!</h3>
              <p className="text-sm text-gray-400">ご注文を受け付けました。確認メールをお送りします。</p>
              <button onClick={handleClose} className="mt-4 px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors">
                閉じる
              </button>
            </div>
          )}
        </div>

        {/* フッターナビ */}
        {currentStep < 3 && (
          <div className="px-4 py-4 pb-[calc(1rem+env(safe-area-inset-bottom))] border-t border-gray-700 flex justify-between items-center">
            <button onClick={currentStep > 1 ? handlePreviousStep : handleClose} className="px-4 py-3 bg-gray-600 text-white rounded hover:bg-gray-500 transition-colors text-base">
              {currentStep > 1 ? '戻る' : 'キャンセル'}
            </button>
            <button
              onClick={handleNextStep}
              disabled={currentStep === 2 && !validateShippingForm()}
              className="px-10 py-4 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 transition-colors text-lg font-bold"
            >
              次へ
            </button>
          </div>
        )}

        {currentStep === 3 && !showStripePayment && (
          <div className="px-4 py-4 pb-[calc(1rem+env(safe-area-inset-bottom))] border-t border-gray-700">
            <button onClick={handlePreviousStep} className="px-4 py-3 bg-gray-600 text-white rounded hover:bg-gray-500 transition-colors text-base">
              戻る
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ArtisanPurchaseModal;
